import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, Dialog, DialogContent, TextField,
  Button, IconButton, CircularProgress, Snackbar, Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';

import { getMisTickets, getMiTicket, responderMiTicket } from '../../actions/tickets';

const CATEGORIA = {
  soporte:    { label: 'Soporte',    bg: '#D6F3E8', color: '#1B7A5A' },
  reporte:    { label: 'Reporte',    bg: '#FF827A', color: '#FFFFFF' },
  aclaracion: { label: 'Aclaración', bg: '#EEF2FF', color: '#3B5BDB' },
};
const ESTADO = {
  abierto:     { label: 'Abierto',     bg: '#E7F0FF', color: '#3B5BDB' },
  en_revision: { label: 'En revisión', bg: '#3B5BDB', color: '#FFFFFF' },
  resuelto:    { label: 'Resuelto',    bg: '#D6F3E8', color: '#1B7A5A' },
  cerrado:     { label: 'Cerrado',     bg: '#EEF1F5', color: '#64748B' },
};

const fechaHora = (iso) =>
  iso ? new Date(iso).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
const fechaCorta = (iso) =>
  iso ? new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const ChipCategoria = ({ c }) => {
  const cfg = CATEGORIA[c] || CATEGORIA.soporte;
  return <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 12, borderRadius: '8px' }} />;
};
const ChipEstado = ({ e }) => {
  const cfg = ESTADO[e] || ESTADO.abierto;
  return <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 12, borderRadius: '8px' }} />;
};

const Adjuntos = ({ adjuntos, claro }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
    {adjuntos.map((a, i) => (
      <Chip key={i} component="a" href={a.url} target="_blank" rel="noopener noreferrer" clickable
        icon={<DescriptionOutlinedIcon sx={{ fontSize: 14 }} />}
        label={a.nombre || `Archivo ${i + 1}`} size="small"
        sx={{
          bgcolor: claro ? 'rgba(255,255,255,0.2)' : '#EEF2FF',
          color: claro ? 'white' : 'primary.main',
          fontWeight: 600, fontSize: 11, maxWidth: 180,
          '& .MuiChip-icon': { color: claro ? 'white' : '#7495F8' }
        }} />
    ))}
  </Box>
);

const MisTickets = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [toast, setToast] = useState(null);

  const [detalle, setDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [texto, setTexto] = useState('');
  const [archivos, setArchivos] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const fileRef = useRef(null);
  const hiloRef = useRef(null);

  const cargar = useCallback(() =>
    getMisTickets()
      .then(setTickets)
      .catch(() => setToast({ tipo: 'error', msg: 'No se pudieron cargar tus tickets.' }))
  , []);

  useEffect(() => { cargar().finally(() => setCargando(false)); }, [cargar]);

  const abrir = useCallback(async (id) => {
    setCargandoDetalle(true);
    setDetalle({ _id: id });
    setTexto(''); setArchivos([]);
    try {
      const t = await getMiTicket(id);
      setDetalle(t);
    } catch {
      setToast({ tipo: 'error', msg: 'No se pudo abrir el ticket.' });
      setDetalle(null);
    } finally {
      setCargandoDetalle(false);
    }
  }, []);

  // Deep-link: abrir el ticket que viene en ?id= (desde una notificación).
  useEffect(() => {
    const id = searchParams.get('id');
    if (id) abrir(id);
  }, [searchParams, abrir]);

  // Deep-link por reserva: ?booking= (botón "Ver ticket" en la reserva) → busca el
  // ticket de esa reserva en la lista y lo abre. Ref para no reabrir al refrescar.
  const deepBookingRef = useRef('');
  useEffect(() => {
    const bookingId = searchParams.get('booking');
    if (!bookingId || deepBookingRef.current === bookingId || !tickets.length) return;
    const t = tickets.find(x => x.booking?._id === bookingId);
    if (t) { deepBookingRef.current = bookingId; abrir(t._id); }
  }, [searchParams, tickets, abrir]);

  // Autoscroll al final del hilo cuando cambian los mensajes.
  useEffect(() => {
    if (hiloRef.current) hiloRef.current.scrollTop = hiloRef.current.scrollHeight;
  }, [detalle?.mensajes?.length]);

  const cerrar = () => {
    setDetalle(null);
    if (searchParams.get('id') || searchParams.get('booking')) {
      searchParams.delete('id');
      searchParams.delete('booking');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const onPickFiles = (e) => {
    const nuevos = Array.from(e.target.files || []);
    setArchivos(prev => [...prev, ...nuevos].slice(0, 5));
    e.target.value = '';
  };

  const responder = async () => {
    if (!texto.trim() && archivos.length === 0) return;
    setEnviando(true);
    try {
      const t = await responderMiTicket(detalle._id, { texto: texto.trim(), archivos });
      setDetalle(t);
      setTexto(''); setArchivos([]);
      cargar();
    } catch (e) {
      setToast({ tipo: 'error', msg: e.response?.data?.msg || 'No se pudo enviar tu mensaje.' });
    } finally {
      setEnviando(false);
    }
  };

  // Ticket finalizado (resuelto o cerrado): ya no se muestra el formulario de respuesta,
  // solo la conversación/información.
  const finalizado = detalle?.estado === 'resuelto' || detalle?.estado === 'cerrado';

  return (
    <Box className="main-page">
      <Box sx={{ bgcolor: '#7495f8', color: 'white', borderRadius: 2, px: 4, py: 5, mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Mis tickets</Typography>
        <Typography mt={2} variant="body1" color="primary">
          <small>Da seguimiento a tus solicitudes de soporte, reportes y aclaraciones, y responde al equipo desde aquí.</small>
        </Typography>
      </Box>

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : tickets.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 3, borderColor: '#E2E8F0' }}>
          <SupportAgentOutlinedIcon sx={{ fontSize: 48, color: '#94A3B8', mb: 1 }} />
          <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>Aún no tienes tickets</Typography>
          <Typography variant="body2" sx={{ color: '#737791', mt: 0.5 }}>
            Cuando reportes un problema o abras una aclaración de una reserva, aquí podrás darles seguimiento.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {tickets.map((t) => {
            const ultimo = t.mensajes?.[t.mensajes.length - 1];
            return (
              <Paper key={t._id} variant="outlined" onClick={() => abrir(t._id)}
                sx={{ p: 2.5, borderRadius: 3, borderColor: '#E2E8F0', cursor: 'pointer', transition: 'box-shadow .15s', '&:hover': { boxShadow: '0 2px 10px rgba(16,36,101,0.08)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#64748B' }}>{t.numero}</Typography>
                  <ChipCategoria c={t.categoria} />
                  <ChipEstado e={t.estado} />
                  <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, color: '#94A3B8' }}>
                    <CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} />
                    <Typography sx={{ fontSize: 12 }}>{fechaCorta(t.updatedAt)}</Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'primary.main' }}>{t.asunto}</Typography>
                {ultimo && (
                  <Typography noWrap sx={{ fontSize: 13, color: '#737791', mt: 0.25 }}>
                    <strong style={{ color: ultimo.esAdmin ? '#3B5BDB' : '#1B7A5A' }}>{ultimo.esAdmin ? 'Soporte: ' : 'Tú: '}</strong>
                    {ultimo.texto || (ultimo.adjuntos?.length ? '📎 Archivo adjunto' : '')}
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Box>
      )}

      {/* ---------- Detalle / conversación ---------- */}
      <Dialog open={!!detalle} onClose={cerrar} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {cargandoDetalle || !detalle?.numero ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', maxHeight: '80vh' }}>
            {/* Encabezado */}
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid #F1F5F9' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#64748B' }}>{detalle.numero}</Typography>
                <ChipCategoria c={detalle.categoria} />
                <ChipEstado e={detalle.estado} />
                <IconButton size="small" onClick={cerrar} sx={{ ml: 'auto' }}><CloseIcon fontSize="small" /></IconButton>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', fontSize: 18 }}>{detalle.asunto}</Typography>
              {detalle.booking && (
                <Typography sx={{ fontSize: 12.5, color: '#94A3B8', mt: 0.25 }}>
                  Reserva: {detalle.booking.servicioNombre} · {detalle.booking.fecha} {detalle.booking.hora}
                </Typography>
              )}
            </Box>

            {/* Hilo */}
            <Box ref={hiloRef} sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5, bgcolor: '#FBFCFE' }}>
              {detalle.descripcion && (
                <Box sx={{ bgcolor: '#F4F8FF', borderRadius: 2, p: 1.5, mb: 2 }}>
                  <Typography sx={{ fontSize: 11.5, color: '#94A3B8', fontWeight: 700, mb: 0.25 }}>Descripción</Typography>
                  <Typography sx={{ fontSize: 13.5, color: '#334155' }}>{detalle.descripcion}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {(detalle.mensajes || []).map((m, i) => (
                  <Box key={i} sx={{ alignSelf: m.esAdmin ? 'flex-start' : 'flex-end', maxWidth: '85%' }}>
                    <Typography sx={{ fontSize: 11.5, color: '#94A3B8', mb: 0.25, textAlign: m.esAdmin ? 'left' : 'right' }}>
                      {m.esAdmin ? (m.autorNombre || 'Soporte') : 'Tú'} · {fechaHora(m.fecha)}
                    </Typography>
                    <Box sx={{ bgcolor: m.esAdmin ? '#F1F5F9' : '#0E2465', color: m.esAdmin ? '#334155' : 'white', p: 1.5, borderRadius: 2, fontSize: 14 }}>
                      {m.texto}
                      {m.adjuntos?.length > 0 && <Adjuntos adjuntos={m.adjuntos} claro={!m.esAdmin} />}
                    </Box>
                  </Box>
                ))}
                {(detalle.mensajes || []).length === 0 && (
                  <Typography sx={{ fontSize: 13, color: '#94A3B8', textAlign: 'center' }}>Todavía no hay mensajes.</Typography>
                )}
              </Box>
            </Box>

            {/* Responder (solo si el ticket sigue abierto/en revisión) */}
            <Box sx={{ borderTop: '1px solid #F1F5F9', p: 2 }}>
              {finalizado ? (
                <Typography sx={{ fontSize: 13, color: '#94A3B8', textAlign: 'center', py: 1 }}>
                  {detalle.estado === 'resuelto'
                    ? 'Este ticket fue resuelto. Si necesitas más ayuda, reporta el problema desde tu reserva.'
                    : 'Este ticket está cerrado. Si necesitas más ayuda, reporta el problema desde tu reserva.'}
                </Typography>
              ) : (
                <>
                  {archivos.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {archivos.map((a, i) => (
                        <Chip key={i} label={a.name} size="small" onDelete={() => setArchivos(prev => prev.filter((_, j) => j !== i))}
                          sx={{ bgcolor: '#EEF2FF', color: 'primary.main', fontWeight: 600, fontSize: 11, maxWidth: 180 }} />
                      ))}
                    </Box>
                  )}
                  <TextField fullWidth multiline minRows={2} placeholder="Escribe tu respuesta al equipo…"
                    value={texto} onChange={(e) => setTexto(e.target.value)} sx={{ mb: 1.5 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input ref={fileRef} type="file" hidden multiple accept="image/*,application/pdf" onChange={onPickFiles} />
                    <Button variant="outlined" startIcon={<AttachFileIcon />} onClick={() => fileRef.current?.click()}
                      sx={{ textTransform: 'none', borderRadius: 2, borderColor: '#E2E8F0', color: '#475569' }}>
                      Adjuntar
                    </Button>
                    <Button variant="contained" startIcon={<SendOutlinedIcon />} onClick={responder}
                      disabled={enviando || (!texto.trim() && archivos.length === 0)}
                      sx={{ ml: 'auto', bgcolor: 'primary.main', textTransform: 'none', fontWeight: 700, borderRadius: 2, '&:hover': { bgcolor: '#0a1b4d' } }}>
                      {enviando ? 'Enviando…' : 'Enviar'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </DialogContent>
        )}
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast && <Alert severity={toast.tipo} onClose={() => setToast(null)}>{toast.msg}</Alert>}
      </Snackbar>
    </Box>
  );
};

export default MisTickets;
